import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { useMemo, useRef, useEffect } from "react";
import { item_listAnimation, listAnimation } from "../../Variables";
import { useNavigate } from "react-router-dom";
import { ParagraphCustom } from "../../ui/atoms/ParagraphCustom";
import useMobile from "../../hooks/useMobile";
import { HeadingCustom } from "../../ui/atoms/HeadingCustom";

export const HorizontalScroller = ({
    type,
    items = [],
    categorySelected,
    title = "Éléments",
    slugPrefix = "/",
    filterByCategory = false,
    categoryField = "category"
}) => {
  const isMobile = useMobile();
  const navigate = useNavigate();
  const ref = useRef();

  const filteredItems = useMemo(() => {
    if (!items) return [];

    const activeItems = items.filter(item => item.status === true);
    if (!categorySelected || !filterByCategory) return activeItems.slice(0, 5);

    const filtered = activeItems.filter(item =>
      item[categoryField]?.includes(categorySelected)
    );

    return filtered.slice(0, 5);
  },[items, categorySelected, categoryField,filterByCategory]);

  const { scrollYProgress } = useScroll({ target: ref });

  const smoothVelocity = useSpring(scrollYProgress, {
    damping: 100,
    stiffness: 400
  });

  const x = useTransform(smoothVelocity, [0, 1], [
    isMobile ? "5%" : "5%",
    isMobile ? "-75%" : "-15%"
  ]);

  const handleShowItem = slug => {
    if (!slug) return;
    navigate(`${slug}`);
  };

  useEffect(() => {
    if (ref.current) {
      window.scrollTo(0, 0);
    }
  }, [categorySelected]);

  return (
    <div
      ref={ref}
      className="w-full lg:h-[300vh] h-[300vh] bg-black flex items-start justify-center gap-10 relative"
    >
      <motion.div
        style={{
          scaleX: scrollYProgress,
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          originX: 0,
          backgroundColor: "#f97316"
        }}
      />
      <div className="sticky top-0 left-0 h-screen w-full flex items-center justify-start overflow-hidden">
        <motion.div style={{ x }} className="flex items-center justify-start min-w-screen">
          <motion.ul
            className="flex items-center gap-10 px-4"
            variants={listAnimation}
            initial="hidden"
            animate="show"
            key={categorySelected}
          >
            {filteredItems?.map((item, index) => (
              <motion.li
                variants={item_listAnimation} 
                transition={{type:"spring", stiffness:400, damping:70, ease:"easeInOut"}}
                key={`${item.id}-${index}`}
                className="cursor-pointer lg:h-[55vh] h-96 w-60 lg:min-w-[350px] lg:rounded-xl relative overflow-hidden"
                onClick={() => handleShowItem(item.slug)}
              >
                <motion.figure
                  className="lg:h-[55vh] h-96 w-60 lg:min-w-[350px]"
                  
                  whileHover={{scale: 1.045 }}    
                  whileTap={{ scale: .945 }}
                  transition={{type:"spring", stiffness:400, damping:70, ease:"easeInOut"}}
                >
                  <img 
                    key={`image-${item.id}-${type}`}
                    src={
                      type === "actus"
                        ? `${process.env.REACT_APP_API_URL}/uploads/${item?.picture}`
                        : `${process.env.REACT_APP_URL_CLOUDFARE}images/${item.mainImage}.webp`
                    }
                    className="lg:opacity-75 opacity-80 w-full h-full lg:rounded-xl rounded-sm object-cover"
                    alt={item.title || "description"}
                  />
                </motion.figure>

                { type === "actus" && 
                  <div className="absolute bottom-4 left-0 w-full lg:px-8 px-2 flex flex-col gap-4 h-auto">
                      <div className="lg:h-14 order-last">
                        <HeadingCustom type="h3" size="small" className="text-slate-300 font-normal max-w-md">{item?.title}</HeadingCustom>
                      </div>
                      <ul className="flex flex-col gap-2">
                        {item?.categories.map((cat, index) => 
                          <li key={index+1} className="lg:text-sm text-xs text-slate-300 bg-slate-100/20 rounded-full w-fit lg:p-2 p-[4px]">{cat}</li>
                        )}
                      </ul>
                  </div>
                }
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        <ParagraphCustom className="text-slate-500 absolute left-1/2 transform -translate-x-1/2 bottom-20 flex items-center gap-4">
          <span className="h-[1px] order-first bg-slate-400 w-10 block"></span>
            {title}
          <span className="h-[1px] bg-slate-400 w-10 block"></span>
        </ParagraphCustom>
      </div>
    </div>
  );
};